import {
  DownwardOutlineIcon,
  type IconProps,
  UpwardOutlineIcon,
} from '@carboncopyco/icons';
import { ColorHex, type Colors, TextBase, TextCaption } from '@carboncopyco/ui';
import classNames from 'classnames';
import { StatsCardItemSkeleton } from './StatsCardItemSkeleton';

export type StatsCardItemProps = {
  title: string;
  value: string | number;
  negative?: boolean;
  loading?: boolean;
  isLast?: boolean;
  reverseTrendColor?: boolean;
} & (
  | { description?: never; percentage?: never }
  | { description: string; percentage?: string | number }
);

export const StatsCardItem = ({
  title,
  value,
  description,
  negative,
  percentage,
  loading,
  isLast,
  reverseTrendColor,
}: StatsCardItemProps) => {
  const getTrendColor: (
    hex?: boolean
  ) =>
    | `#${string}`
    | Extract<Colors, 'text-ds-danger-300' | 'text-ds-accent2-400'> = (hex) => {
    const red = hex ? ColorHex.danger300 : 'text-ds-danger-300';
    const green = hex ? ColorHex.accent2400 : 'text-ds-accent2-400';
    if (reverseTrendColor) {
      return negative ? green : red;
    }
    return negative ? red : green;
  };

  return (
    <div
      className={classNames('bg-ds-accent3-100', {
        'rounded-b-lg': isLast,
      })}
    >
      {loading ? (
        <StatsCardItemSkeleton />
      ) : (
        <div className="flex items-center p-6 h-20">
          <div className="flex flex-col gap-2">
            <TextBase
              label={title}
              color="text-ds-natural-600"
            />
            {description && (
              <div className="flex items-center gap-1">
                {!!percentage && (
                  <div className="flex items-center gap-1">
                    {negative ? (
                      <DownwardOutlineIcon
                        size={12}
                        color={getTrendColor(true) as IconProps['color']}
                      />
                    ) : (
                      <UpwardOutlineIcon
                        size={12}
                        color={getTrendColor(true) as IconProps['color']}
                      />
                    )}
                    <TextCaption
                      label={`${percentage}%`}
                      color={getTrendColor() as Colors}
                    />
                  </div>
                )}
                <TextCaption label={description} />
              </div>
            )}
          </div>
          <p className="ml-auto">{value}</p>
        </div>
      )}
    </div>
  );
};
